import { Injectable } from '@angular/core';
import { Observable, of, interval, Subscription } from 'rxjs';
import { catchError, map, tap, filter, switchMap, startWith, flatMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { IMenu } from '../../../models/menu.interface';
import { environment } from 'src/environments/environment';
import { INext } from 'src/app/models/next.interface';
import { MonitoringService } from '../../utility/monitoring/monitoring.service';
import {IIdentify} from '../../../models/identify.interface';
import {select, Store} from '@ngrx/store';
import {selectIdentify} from '../../../store/selectors/identify.selectors';
import {IAppState} from '../../../store/state/app.state';
import {UrlControllerService} from '../../url-controller.service';
import sortBy from 'lodash/sortBy';

/**
 * Service with http call to populate menu and next sections
 */
@Injectable({
  providedIn: 'root',
})
export class MenuService extends UrlControllerService{
  public identify$: Observable<IIdentify>;
  /**
   * Url of api
   */
  private raceUrl: string;
  private today: boolean = true;
  public statoQf = '0';
  public statoTot = '0';
  public statoPsr = '0';
  private token = null;
  public apiUrlMenu: string;

  /**
   * Costructor service
   * @param http take http client module
   * @param monitoringService service to log and monitoring
   */
  constructor(public _store: Store<IAppState>, private http: HttpClient, private monitoringService: MonitoringService) {
    super(_store);
    this.apiUrlMenu = this.apiUrlSelected + 'rest/program/';
    this.raceUrl = 'races';
    this.identify$ = this._store.pipe(select(selectIdentify));
    this.identify$.subscribe((x) => {
      if (x) {
        this.statoQf = x.stato_qf;
        this.statoTot = x.stato_tot;
        this.statoPsr = x.stato_psr;
        this.token = x.token;
      }
    });
  }

  /**
   * Call service and return observable of IMenu
   * @returns Observable<IMenu>
   */
  getRaces(date): Observable<IMenu> {
    if (date && date !== 'reset') {
      this.raceUrl = 'archive/' + date;
      this.today = false;
    } else if (date && date === 'reset') {
      this.today = true;
    }
    if (this.today) {
      this.raceUrl = 'races';
    }

    return this.http.get<any>(this.apiUrlSelected + 'rest/program/' + this.raceUrl).pipe(
      map((result) => {
        if (result && result.country) {
          // Controllo se è un array (a volte arriva un solo elemento)
          if (!Array.isArray(result.country)) {
            result.country = [result.country];
          }
          const antepost = [];
          // Ciclo le corse e raggruppo le gare per nome
          // A causa dei fusi orari più gare con lo stesso nome
          result.country.forEach(function (element) {
            if (element && element.course) {
              element.course.map((x) => (x.group = x.name + x.tp));
              // In ogni corsa elimino le race che non hanno quote

              element.course.forEach((elemento) => {
                elemento.race = elemento.race
                  ? elemento.race.filter(
                      (x) =>
                        (Number(x.pers.palinsesto) > 0 && x.pers.avvenimento > 0) ||
                        x.pers.tot === '1' ||
                        (environment.mstCID.includes(this.token)) || !this.today
                    )
                  : [];
              }, this);
              element.course.forEach((elemento) => {
                elemento.race = elemento.race ? elemento.race.filter((x) => {
                  x.position = Number(x.number);
                  return this.availableRace(x);
                }, this) : [];
              }, this);

              antepost[element.id] = JSON.parse(JSON.stringify(element.course));
              antepost[element.id] = antepost[element.id].filter((x) => x.antepost && x.antepost === '1');

              if (antepost[element.id].length > 0) {
                element.course = element.course.filter((x) => x.antepost && x.antepost === '0');
              }

              // console.log(antepost);

              element.course = _.chain(element.course)
                .filter((x) => x.race && x.race.length > 0)
                .sortBy('race[0].date')
                .groupBy('group')
                .map((x) => {
                  if (x.length > 1) {
                    let race = [];
                    x.forEach((course) => {
                      race = race.concat(course.race);
                    });
                    x[0].race = race;
                  }
                  return x[0];
                })
                .value();

              // Ordino per Numero corsa
              element.course.forEach((x) => {
                x.race = sortBy(x.race, 'position');
              }, this);
            } else {
              throw new Error('No race');
            }
          }, this);

          // Elimino country le cui corse hanno 0 elementi
          result.country = result.country.filter((x) => x.course.length > 0);

          if (antepost.length > 0) {
            result.antepost = [];
            antepost.forEach((x, index) => {
              if (
                  x.length > 0
              ) {
                result.antepost.push({id: index, course: x});
              }
            });
            if (this.today) {
              result.antepost.forEach((x) => {
                x.course = x.course.filter((course) => {
                  course.race = course.race.filter((race) => race.pers && (race.ss === 'AP' && race.pers.ss !== 'CH'));
                  return course.race.length > 0;
                });
              });
            }
          }
          // Aggiungo in testa una gara vuota
          result.country.splice(0, 0, { id: 0, country: 'All' });
          return result;
        } else {
          throw new Error('No race');
        }
      }),
      catchError(
        this.monitoringService.handleError<IMenu>('getRaces', { country: [], antepost: [] })
      )
    );
  }

  /**
   * Call service and return observable of IMenu
   * @returns Observable<IMenu>
   */
  getNext(): Observable<INext> {
    return this.http.get<any>(this.apiUrlSelected + 'rest/program/' + 'next').pipe(
      map((x) => {
        if (!x || !x.race) {
          throw Error('no next');
        }
        // console.log(x);
        x.race = x.race.filter((race) => this.availableRace(race));
        return x;
      }),
      catchError(
        this.monitoringService.handleError<INext>('getNext', { race: [] })
      )
    );
  }

  availableRace(race): boolean {
    return ( (this.statoQf === '1' && (Number(race.pers.pal_online) > 0 && Number(race.pers.avv_online) > 0))
        || (this.statoTot === '1' && race.pers.tot === '1' && Number(race.palinsesto) > 0 && Number(race.avvenimento) > 0)
        || (this.statoPsr === '1' && race.pers.tot === '1' && Number(race.pal_psr) > 0 && Number(race.conc_psr) > 0)
        || (environment.mstCID.includes(this.token))
        || !this.today
    );
  }

}
